import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { 
  Users, 
  Trash2, 
  Search, 
  AlertTriangle, 
  CheckCircle, 
  RefreshCw,
  Info,
  Mail,
  Building2,
  Calendar,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OrphanUsersManagement() {
  const [orphanUsers, setOrphanUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchEmail, setSearchEmail] = useState('');
  const [emailCheckResult, setEmailCheckResult] = useState(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [showCleanupDialog, setShowCleanupDialog] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchOrphanUsers = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/orphan-users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch orphan users');
      
      const data = await response.json();
      setOrphanUsers(data.orphan_users || []);
    } catch (error) {
      toast.error('Failed to load orphan users');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrphanUsers();
  }, [fetchOrphanUsers]);

  const checkEmailStatus = async () => {
    if (!searchEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    setCheckingEmail(true);
    setEmailCheckResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/check-email/${encodeURIComponent(searchEmail)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error('Failed to check email');
      
      const data = await response.json();
      setEmailCheckResult(data);
    } catch (error) {
      toast.error('Failed to check email status');
      console.error(error);
    } finally {
      setCheckingEmail(false);
    }
  };

  const deleteOrphanUser = async (userId, forceDelete = false) => {
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const url = forceDelete 
        ? `${API_URL}/api/admin/orphan-users/${userId}?force=true`
        : `${API_URL}/api/admin/orphan-users/${userId}`;
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to delete user');
      }
      
      toast.success(`User ${data.email} deleted successfully${data.force_used ? ' (forced)' : ''}`);
      setShowDeleteDialog(false);
      setSelectedUser(null);
      setEmailCheckResult(null);
      fetchOrphanUsers();
    } catch (error) {
      toast.error(error.message || 'Failed to delete user');
      console.error(error);
    } finally {
      setDeleting(false);
    }
  };

  const cleanupAllOrphanUsers = async () => {
    setCleaningUp(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/orphan-users/cleanup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error('Cleanup failed');
      
      const data = await response.json();
      toast.success(`Cleanup complete: ${data.deleted_count} users deleted`);
      setShowCleanupDialog(false);
      fetchOrphanUsers();
    } catch (error) {
      toast.error('Cleanup failed');
      console.error(error);
    } finally {
      setCleaningUp(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      available: <Badge className="bg-green-500">Available</Badge>,
      in_use: <Badge className="bg-blue-500">In Use</Badge>,
      orphan: <Badge className="bg-yellow-500">Orphan</Badge>,
      member_only: <Badge className="bg-purple-500">Member Only</Badge>
    };
    return badges[status] || <Badge>Unknown</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6 text-orange-500" />
            Orphan Users Management
          </h2>
          <p className="text-muted-foreground mt-1">
            Clean up user accounts that block email reuse
          </p>
        </div>
        <Button onClick={fetchOrphanUsers} variant="outline" disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-500 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">What are orphan users?</p>
              <p className="text-sm text-blue-700 mt-1">
                Orphan users are login accounts (user records) that have no associated member profile, 
                usually from incomplete deletions or registration errors. These accounts block the registration 
                of new members with the same email. Deleting them allows email reuse.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email Check Tool */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Search className="h-5 w-5" />
            Check Email Status
          </CardTitle>
          <CardDescription>
            Verify if an email is available, in use, or has an orphan user account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              placeholder="email@example.com"
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && checkEmailStatus()}
              className="max-w-md"
            />
            <Button onClick={checkEmailStatus} disabled={checkingEmail}>
              {checkingEmail ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Check
                </>
              )}
            </Button>
          </div>
          
          {emailCheckResult && (
            <div className="mt-4 p-4 border rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <span className="font-medium">{emailCheckResult.email}</span>
                {getStatusBadge(emailCheckResult.status)}
              </div>
              <p className="text-sm text-muted-foreground mb-2">
                {emailCheckResult.message}
              </p>
              {emailCheckResult.details && (
                <div className="text-xs text-muted-foreground mb-3 bg-gray-50 p-2 rounded">
                  <p><strong>User ID:</strong> {emailCheckResult.details.user_id || 'N/A'}</p>
                  <p><strong>Role:</strong> {emailCheckResult.details.user_role || 'N/A'}</p>
                  <p><strong>Clinic ID:</strong> {emailCheckResult.details.clinic_id || 'N/A'}</p>
                  <p><strong>Can Delete:</strong> {emailCheckResult.details.can_delete ? 'Yes' : 'No (use Force Delete)'}</p>
                </div>
              )}
              {emailCheckResult.status === 'orphan' && (
                <div className="flex gap-2">
                  {emailCheckResult.details?.can_delete && (
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={() => {
                        setSelectedUser({ 
                          id: emailCheckResult.details.user_id, 
                          email: emailCheckResult.email,
                          force: false
                        });
                        setShowDeleteDialog(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-red-300 text-red-600 hover:bg-red-50"
                    onClick={() => {
                      setSelectedUser({ 
                        id: emailCheckResult.details.user_id, 
                        email: emailCheckResult.email,
                        force: true
                      });
                      setShowDeleteDialog(true);
                    }}
                  >
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    Force Delete
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Orphan Users List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-500" />
                Orphan Users ({orphanUsers.length})
              </CardTitle>
              <CardDescription>
                User accounts without an associated member profile
              </CardDescription>
            </div>
            {orphanUsers.length > 0 && (
              <Button 
                variant="destructive" 
                onClick={() => setShowCleanupDialog(true)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Clean Up All ({orphanUsers.length})
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : orphanUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <CheckCircle className="h-12 w-12 text-green-500 mb-3" />
              <p className="text-lg font-medium">All Clean!</p>
              <p className="text-sm text-muted-foreground">
                No orphan users in the system
              </p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Clinic</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orphanUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{user.email}</span>
                        </div>
                      </TableCell>
                      <TableCell>{user.full_name || '-'}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          {user.clinic_name || '-'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => {
                            setSelectedUser(user);
                            setShowDeleteDialog(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Single User Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-red-500" />
              {selectedUser?.force ? 'Force Delete User' : 'Confirm Deletion'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the user account{' '}
              <strong>{selectedUser?.email}</strong>?
              {selectedUser?.force && (
                <>
                  <br /><br />
                  <span className="text-red-600 font-medium">
                    ⚠️ Force delete will remove the user even if they have associated member profiles or a non-patient role.
                  </span>
                </>
              )}
              <br /><br />
              This action is irreversible and will free up the email for new registration.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteOrphanUser(selectedUser?.id, selectedUser?.force)}
              disabled={deleting}
            >
              {deleting ? (
                <RefreshCw className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cleanup All Dialog */}
      <Dialog open={showCleanupDialog} onOpenChange={setShowCleanupDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Bulk Orphan User Cleanup
            </DialogTitle>
            <DialogDescription className="pt-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-yellow-800 font-medium">
                  ⚠️ This will delete {orphanUsers.length} orphan user accounts.
                </p>
              </div>
              <p className="text-sm">
                This will free up {orphanUsers.length} email addresses that are currently 
                blocking new member registrations.
              </p>
              <p className="text-sm mt-2 font-medium">
                This action is irreversible. Do you want to continue?
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowCleanupDialog(false)}
              disabled={cleaningUp}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={cleanupAllOrphanUsers}
              disabled={cleaningUp}
            >
              {cleaningUp ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                  Cleaning...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clean Up All ({orphanUsers.length})
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
